import BaseVm from '../BaseVm'
import { node } from '../../../utils/dom'
import { queryString } from '../../../utils/window'
import GoogleTagService from '../../services/GoogleTagService'
import { AD_FORMATS, CLASS_NAMES } from '../../config/adsConfig'
import { SELECTORS } from '../../../config/preRenderedSelectorsConfig'
import { getIsMobile, getIsTabletInPortrait, getIsDesktop, getIsTablet } from '../../../utils/ua'
import { adStore, EMPTY_SLOT_DIMENSIONS, EMPTY_SLOT_SIZES_OBJECT } from '../../stores/adStore'
import { allSlotsRendered, slotRendered } from '../../actions/adActions'
import { dfpRenderEvent } from '../../../../jsCore/organizedByType/actions/analyticsActions'
import { dispatch } from '../../../../jsCore/application/appStore'
const { adsDebugSizes } = queryString
const HEADER_ADDED_HEIGHT = 24
const { EMPTY: EMPTY_AD } = CLASS_NAMES

function isDfpEventSize1x1(dfpEvent) {
  return (
    dfpEvent.size && dfpEvent.size.length > 1 && dfpEvent.size[0] === 1 && dfpEvent.size[1] === 1
  )
}

function isEmptyAd(dfpEvent) {
  return dfpEvent.isEmpty || isDfpEventSize1x1(dfpEvent)
}

function getAdSlotSizeFromDfpEvent(dfpEvent) {
  const [width = 0, height = 0] = dfpEvent.size
  return { width, height }
}

function setSizeToZeroIfEmpty(dfpEvent) {
  dfpEvent.size = isEmptyAd(dfpEvent) ? EMPTY_SLOT_DIMENSIONS : dfpEvent.size
}

export default class AdVm extends BaseVm {
  constructor(options = {}) {
    super(options)
    const { googletag: gTag } = window
    const { actions, adId, adConfig: adData, type } = options
    this.merge({
      actions,
      adData,
      adId,
      gTag,
      type,
    })
    this.gTagService = GoogleTagService.create()
  }

  onChange(
    payload = {
      event: { isEmpty: null, slideshowRefreshAction: false, resetPageTargetingLimit: false },
    }
  ) {
    const { adId, event: { isEmpty, slideshowRefreshAction, resetPageTargetingLimit } } = payload
    if (isEmpty !== null && adId === this.adId) {
      this.emptySlot = {
        isEmpty,
        adId,
      }
    }
    if (slideshowRefreshAction && adStore.shouldRefreshSlot) {
      adStore.state.topSlotHeight = false
      this.gTagService.refreshSlot(this.gTagService.slots[this.adId])
    }
    if (resetPageTargetingLimit) {
      this.gTagService.resetPageTargetingLimit()
    }
    return super.onChange()
  }

  get emptySlot() {
    return (adStore.state[this.adId] || {}).emptySlot || false
  }

  set emptySlot(emptySlot) {
    adStore.state[this.adId] = adStore.state[this.adId] || {}
    adStore.state[this.adId].emptySlot = emptySlot
  }

  get topHeight() {
    return adStore.state.topSlotHeight
  }

  get adSize() {
    return (adStore.state[this.adId] || {}).adSize || EMPTY_SLOT_SIZES_OBJECT
  }

  initSlot() {
    const { adId, type, adData } = this
    this.gTagService.initSlot(adData, type, adId)
    return this
  }

  display() {
    this.initTargeting()
    return this
  }

  initAndDisplay() {
    const headerAd = node(SELECTORS.HEADER_AD_UNIT)
    this.initSlot().display()
    if (!headerAd) {
      return this
    }
    if (adStore.isAdblockPresent) {
      headerAd.classList.add(EMPTY_AD)
    }

    return this
  }

  initDisplayAndHideOnTimeOut(timeout) {
    this.initAndDisplay()
    setTimeout(
      function hideAdOnTimeout() {
        this.hide()
      }.bind(this),
      timeout
    )
    return this
  }

  hide() {
    this.actions.slotHide(this.adId)
    return this
  }

  shouldReportSlotData(dfpEvent) {
    return (
      (dfpEvent.creativeId !== null || dfpEvent.sourceAgnosticCreativeId !== null) &&
      (dfpEvent.lineItemId !== null || dfpEvent.sourceAgnosticLineItemId !== null) &&
      !dfpEvent.isEmpty
    )
  }

  normlizeDfpData(data) {
    return data !== null ? data : 'none'
  }

  getCustomDimension(dfpEvent) {
    return {
      advertiserId: this.normlizeDfpData(dfpEvent.advertiserId),
      campaignId: this.normlizeDfpData(dfpEvent.campaignId),
      creativeId: this.normlizeDfpData(dfpEvent.creativeId),
      isBackfill: this.normlizeDfpData(dfpEvent.isBackfill),
      isEmpty: this.normlizeDfpData(dfpEvent.isEmpty),
      lineItemId: this.normlizeDfpData(dfpEvent.lineItemId),
      serviceName: this.normlizeDfpData(dfpEvent.serviceName),
      sizeW: this.normlizeDfpData(dfpEvent.size[0]),
      sizeH: this.normlizeDfpData(dfpEvent.size[1]),
      adUnitName: dfpEvent.slot.L,
      sourceAgnosticCreativeId: this.normlizeDfpData(dfpEvent.sourceAgnosticCreativeId),
      sourceAgnosticLineItemId: this.normlizeDfpData(dfpEvent.sourceAgnosticLineItemId),
    }
  }

  reportSlotRenderToGA(dfpEvent) {
    if (this.shouldReportSlotData(dfpEvent)) {
      dispatch(dfpRenderEvent(this.getCustomDimension(dfpEvent)))
    }
  }

  dfpSlotRenderEndedEvent(dfpEvent) {
    const { adId } = this
    if (dfpEvent.slot !== this.gTagService.slots[adId]) {
      return this
    }
    adsDebugSizes && this.warn('dfpSlotRenderEndedEvent:', adId, dfpEvent)
    const { isEmpty: isAdEmpty } = dfpEvent
    setSizeToZeroIfEmpty(dfpEvent)
    slotRendered({
      adId,
      isAdEmpty,
      size: getAdSlotSizeFromDfpEvent(dfpEvent),
      adRenderedEvent: dfpEvent,
    })
    this.reportSlotRenderToGA(dfpEvent)
    this.gTagService.renderedAdsEvents[adId] = dfpEvent
    const isAllSlotsRendered =
      Object.keys(this.gTagService.slots).length ===
      Object.keys(this.gTagService.renderedAdsEvents).length
    if (isAllSlotsRendered) {
      // TODO: Remove this bad interstitial hack once the interstitial rendering code is moved from the SLIM to the client JS code
      if (window.__NMinterstitialEventHack) {
        this.gTagService.renderedAdsEvents.Interstitial = window.__NMinterstitialEventHack
      }
      allSlotsRendered(this.gTagService.renderedAdsEvents)
    }
    return this
  }

  initTargeting() {
    const { adId, adData, type } = this
    this.gTagService.initTargeting(adData, adId, this.dfpSlotRenderEndedEvent.bind(this), type)
    return this
  }

  get stickyConf() {
    const isStickyOnPlatformMap = this.adData.isSticky || {}
    if (!Object.keys(isStickyOnPlatformMap).length) {
      return false
    }
    let stickyPlatform
    if (getIsTabletInPortrait()) {
      stickyPlatform = 'portraitTablet'
    } else if (getIsMobile()) {
      stickyPlatform = 'mobile'
    } else {
      stickyPlatform = 'desktop'
    }
    return isStickyOnPlatformMap[stickyPlatform]
  }
}



// WEBPACK FOOTER //
// ./provolution/app/views/provolution/jsCore/organizedByType/vms/ads/AdVm.js